class Accordion extends HTMLElement {
  constructor() {
    super();
    this.imageWrapper = null;
    this.defaultImageSrc = null;
    this.defaultImageAlt = null;
    this.accordionImages = new Map();
  }

  connectedCallback() {
    this.imageWrapper = this.querySelector('.accordion-v2__image-wrapper');
    if (!this.imageWrapper) return;

    // Store default image
    const defaultImg = this.imageWrapper.querySelector('img');
    if (defaultImg) {
      this.defaultImageSrc = defaultImg.src;
      this.defaultImageSrcset = defaultImg.srcset || '';
      this.defaultImageSizes = defaultImg.sizes || '';
      this.defaultImageAlt = defaultImg.alt;
    }

    // Collect all accordion images
    const details = this.querySelectorAll('details');
    details.forEach((detail) => {
      const imageUrl = detail.getAttribute('data-accordion-image-url');
      const imageSrcset = detail.getAttribute('data-accordion-image-srcset') || '';
      const imageAlt = detail.getAttribute('data-accordion-image-alt') || '';
      if (imageUrl) {
        this.accordionImages.set(detail, {
          src: imageUrl,
          srcset: imageSrcset,
          alt: imageAlt
        });
      }
    });

    // Listen for accordion toggle events
    let isClosingOthers = false;
    details.forEach((detail) => {
      detail.addEventListener('toggle', (e) => {
        // Skip if we're programmatically closing other accordions
        if (isClosingOthers) return;
        
        if (detail.open) {
          // Close all other accordions first
          isClosingOthers = true;
          details.forEach((otherDetail) => {
            if (otherDetail !== detail && otherDetail.open) {
              otherDetail.open = false;
            }
          });
          isClosingOthers = false;
          
          // Use setTimeout to ensure the toggle event has fully processed
          setTimeout(() => {
            this.switchImage(detail);
          }, 0);
        }
      });
    });

    // Set initial image (first open accordion or default)
    const firstOpen = this.querySelector('details[open]');
    if (firstOpen) {
      this.switchImage(firstOpen);
    } else if (this.defaultImageSrc) {
      this.setImage(this.defaultImageSrc, this.defaultImageSrcset || '', this.defaultImageAlt);
    }
  }

  switchImage(detailElement) {
    if (!detailElement) return;
    
    // Get image data directly from the attributes
    const imageUrl = detailElement.getAttribute('data-accordion-image-url');
    const imageSrcset = detailElement.getAttribute('data-accordion-image-srcset') || '';
    const imageAlt = detailElement.getAttribute('data-accordion-image-alt') || '';
    
    if (imageUrl) {
      this.setImage(imageUrl, imageSrcset, imageAlt);
    } else {
      // Try to get from map
      const imageData = this.accordionImages.get(detailElement);
      if (imageData) {
        this.setImage(imageData.src, imageData.srcset || '', imageData.alt);
      } else if (this.defaultImageSrc) {
        this.setImage(this.defaultImageSrc, this.defaultImageSrcset || '', this.defaultImageAlt);
      }
    }
  }

  setImage(src, srcset, alt) {
    if (!this.imageWrapper) {
      console.warn('Image wrapper not found');
      return;
    }

    const img = this.imageWrapper.querySelector('img');
    if (!img) {
      console.warn('Image element not found');
      return;
    }

    // Check if image is actually changing
    const currentSrc = img.src.split('?')[0];
    const newSrc = src.split('?')[0];
    
    if (currentSrc === newSrc && img.src === src) {
      // Same image, no need to update
      return;
    }

    // Fade out current image
    img.classList.remove('loaded');
    img.classList.add('loading');
    img.style.opacity = '0.5';

    // Preload new image
    const newImg = new Image();
    newImg.onload = () => {
      // Update image attributes
      img.src = src;
      if (srcset) {
        img.srcset = srcset;
      } else {
        img.removeAttribute('srcset');
      }
      if (alt !== undefined) {
        img.alt = alt;
      }
      if (!img.sizes) {
        img.sizes = '(min-width: 990px) 50vw, 100vw';
      }

      // Fade in new image
      requestAnimationFrame(() => {
        img.classList.remove('loading');
        img.classList.add('loaded');
        img.style.opacity = '1';
      });
    };

    newImg.onerror = () => {
      // If image fails to load, still try to set it
      img.src = src;
      if (srcset) img.srcset = srcset;
      if (alt !== undefined) img.alt = alt;
      img.classList.remove('loading');
      img.style.opacity = '1';
    };

    // Start loading
    if (srcset) {
      newImg.srcset = srcset;
      newImg.sizes = img.sizes || '(min-width: 990px) 50vw, 100vw';
    }
    newImg.src = src;
  }
}

customElements.define('x-accordion', Accordion);

